// ============================================================================
// API MANAGEMENT PAGES
//
// Extracted from index.html. Loaded as <script type="text/babel" src="./api-mgmt.js">.
// Defines: ApiProxiesPage, WebhookDeliveriesPage.
//
// Depends on: API, fmtDate (defined in main script as globals).
// React is loaded as a global from CDN.
// ============================================================================

// ============================================================================
// RAW FETCH HELPER — mirrors Postman's request builder (no JSON coercion).
// Use this for testing endpoints directly; the `API()` wrapper above sends
// JSON bodies and throws on non-2xx, which isn't useful for exploring.
// ============================================================================
const rawFetch=async(method,url,opts={})=>{
  const t0=performance.now();
  const resp=await fetch(url,{
    method:method.toUpperCase(),
    headers:{'Content-Type':'application/json','Accept':'application/json,text/plain,*/*',...(opts.headers||{})},
    body:(opts.body&&['GET','HEAD'].indexOf(method.toUpperCase())<0)?opts.body:undefined,
    mode:'cors',
    credentials:'same-origin',
  });
  const ms=Math.round(performance.now()-t0);
  const ct=(resp.headers.get('content-type')||'').toLowerCase();
  const isJson=ct.includes('application/json');
  let body=null;
  try{ body=isJson?await resp.json():await resp.text(); }
  catch{ body=await resp.text(); }
  return{
    status:resp.status,statusText:resp.statusText,
    headers:Object.fromEntries([...resp.headers.entries()]),
    body,isJson,ms
  };
};

// `var`, not `const`: every text/babel script shares one global scope, so a second
// `const` binding of these names is a redeclaration SyntaxError now that Babel compiles
// JSX only (data-presets="react") instead of downlevelling to ES5.
var {useState,useEffect,useCallback,useRef,useMemo}=React;
// Intentionally `var`, not `const` — shared global scope; see the note in index.html.
var TAXLAB_CTA_STYLE={
  fontFamily:'"Public Sans", sans-serif',
  fontWeight:600,
  fontSize:14,
  border:'none',
  cursor:'pointer',
  opacity:1,
  borderRadius:10,
  padding:'9px 18px',
  display:'inline-flex',
  alignItems:'center',
  justifyContent:'center',
  gap:8,
  transition:'0.18s',
  whiteSpace:'nowrap',
  width:'auto',
  height:40,
  lineHeight:1.4,
  boxSizing:'border-box',
  background:'linear-gradient(90deg, rgb(237, 76, 139) 0%, rgb(241, 91, 94) 25%, rgb(247, 108, 43) 50%, rgb(249, 114, 23) 100%)',
  color:'rgb(255, 255, 255)',
};
// Intentionally `var`, not `const` — shared global scope; see the note in index.html.
var TAXLAB_LIGHT_STYLE={
  fontFamily:'"Public Sans", sans-serif',
  fontWeight:600,
  fontSize:14,
  border:'1px solid rgb(196, 205, 213)',
  cursor:'pointer',
  opacity:1,
  borderRadius:10,
  padding:'9px 18px',
  display:'inline-flex',
  alignItems:'center',
  justifyContent:'center',
  gap:8,
  transition:'0.18s',
  whiteSpace:'nowrap',
  width:'auto',
  height:40,
  lineHeight:1.4,
  boxSizing:'border-box',
  background:'rgb(255, 255, 255)',
  color:'rgb(29, 37, 46)',
};
/* Compact table-row actions. Inline so they win over a stale styles.css or the
 * old test-pane.js injected .btn / .btn-sm rules still on some deploys. */
const TAXLAB_ROW_BTN={
  fontFamily:'"Public Sans", sans-serif',
  fontWeight:700,
  fontSize:13,
  cursor:'pointer',
  opacity:1,
  borderRadius:8,
  padding:'6px 12px',
  display:'inline-flex',
  alignItems:'center',
  justifyContent:'center',
  gap:8,
  transition:'0.18s',
  whiteSpace:'nowrap',
  width:'auto',
  height:'auto',
  lineHeight:1.4,
  boxSizing:'border-box',
};
const TAXLAB_ROW_BTN_LIGHT={
  ...TAXLAB_ROW_BTN,
  border:'1px solid rgb(196, 205, 213)',
  background:'rgb(255, 255, 255)',
  color:'rgb(29, 37, 46)',
};
const TAXLAB_ROW_BTN_GREY={
  ...TAXLAB_ROW_BTN,
  border:'none',
  background:'rgba(29, 37, 46, .1)',
  color:'rgb(26, 33, 41)',
};
const TAXLAB_ROW_ICON_BTN={
  fontFamily:'"Public Sans", sans-serif',
  fontWeight:700,
  fontSize:13,
  cursor:'pointer',
  opacity:1,
  border:'none',
  borderRadius:8,
  padding:0,
  minWidth:32,
  width:32,
  height:32,
  display:'inline-flex',
  alignItems:'center',
  justifyContent:'center',
  flexShrink:0,
  boxSizing:'border-box',
  background:'rgba(29, 37, 46, .1)',
  color:'rgb(26, 33, 41)',
};
function ProxyRowActions({onTest,onCopy,onEdit,onOpen,onCode}){
  return (
    <div style={{display:'inline-flex',gap:8,alignItems:'center',justifyContent:'flex-end'}}>
      <button className="int-row-btn int-row-btn-light" style={TAXLAB_ROW_BTN_LIGHT} onClick={onTest} title="Send a test request (Postman-like)">
        <BeakerActionIcon size={17}/> Test
      </button>
      <button className="int-row-btn int-row-btn-light" style={TAXLAB_ROW_BTN_LIGHT} onClick={onCopy}><ApiCopyIcon size={17}/> Copy</button>
      <button className="btn btn-ghost row-icon-action" style={TAXLAB_ROW_ICON_BTN} onClick={onEdit}
        title="Edit this proxy here — name and path" aria-label="Edit"><PencilIcon size={17}/></button>
      {onCode && (
        <button className="btn btn-ghost row-icon-action" style={TAXLAB_ROW_ICON_BTN} onClick={onCode}
          title="Open Java DSL code editor" aria-label="Code"><CodeIcon size={17}/></button>
      )}
      {onOpen && (
        <button className="int-row-btn int-row-btn-grey" style={TAXLAB_ROW_BTN_GREY}
          onClick={onOpen}
          title="Open the full integration editor (connections, trigger, visual builder)">
          <ExternalLinkIcon size={17}/> Open
        </button>
      )}
    </div>
  );
}
function ApiCopyIcon({size=14,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <path fill="currentColor" d="M7 7c0-2.828 0-4.243.879-5.121C8.757 1 10.172 1 13 1h4c2.828 0 4.243 0 5.121.879C23 2.757 23 4.172 23 7v4c0 2.828 0 4.243-.879 5.121C21.243 17 19.828 17 17 17h-4c-2.828 0-4.243 0-5.121-.879C7 15.243 7 13.828 7 11z" opacity=".5"></path>
      <path fill="currentColor" d="M1 13c0-2.828 0-4.243.879-5.121C2.757 7 4.172 7 7 7h4c2.828 0 4.243 0 5.121.879C17 8.757 17 10.172 17 13v4c0 2.828 0 4.243-.879 5.121C15.243 23 13.828 23 11 23H7c-2.828 0-4.243 0-5.121-.879C1 21.243 1 19.828 1 17z"></path>
    </svg>
  );
}
function ApiInboxIcon({size=36,color='rgb(99,115,129)'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <path fill="currentColor" d="M3 5h18v14H3zm2 2v2h14V7zm0 4v2h8v-2zm0 4v2h6v-2zm11-1 3 2-3 2z" opacity=".92"></path>
    </svg>
  );
}
function ApiUsersIcon({size=36,color='rgb(99,115,129)'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <path fill="currentColor" d="M15.5 7.5a3.5 3.5 0 1 1-7 0a3.5 3.5 0 0 1 7 0"></path>
      <path fill="currentColor" d="M19.5 7.5a2.5 2.5 0 1 1-5 0a2.5 2.5 0 0 1 5 0m-15 0a2.5 2.5 0 1 0 5 0a2.5 2.5 0 0 0-5 0" opacity=".4"></path>
      <path fill="currentColor" d="M18 16.5c0 1.933-2.686 3.5-6 3.5s-6-1.567-6-3.5S8.686 13 12 13s6 1.567 6 3.5"></path>
      <path fill="currentColor" d="M22 16.5c0 1.38-1.79 2.5-4 2.5s-4-1.12-4-2.5s1.79-2.5 4-2.5s4 1.12 4 2.5m-20 0C2 17.88 3.79 19 6 19s4-1.12 4-2.5S8.21 14 6 14s-4 1.12-4 2.5" opacity=".4"></path>
    </svg>
  );
}
function ApiTrashIcon({size=14,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <path fill="currentColor" d="M9 3h6l1 2h4v2H4V5h4z" opacity=".55"></path>
      <path fill="currentColor" d="M6 8h12l-1 11a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2zm4 3v7h2v-7zm4 0v7h2v-7z"></path>
    </svg>
  );
}
function ApiRotateIcon({size=14,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12a8 8 0 0 1 14-5.3M20 12a8 8 0 0 1-14 5.3M17 4v3h-3M7 20v-3h3"></path>
    </svg>
  );
}
function ApiRevokeIcon({size=14,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <circle cx="12" cy="12" r="9" fill="currentColor" opacity=".25"></circle>
      <path d="M6.5 17.5l11-11" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"></path>
    </svg>
  );
}
function ApiWarningIcon({size=14,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <path fill="currentColor" d="M13 3.5a1.15 1.15 0 0 0-2 0l-8.5 15A1.15 1.15 0 0 0 3.5 20h17a1.15 1.15 0 0 0 1-1.5z" opacity=".4"></path>
      <path fill="currentColor" d="M11.25 9h1.5v5h-1.5zm0 6.5h1.5V17h-1.5z"></path>
    </svg>
  );
}
function CodeIcon({size=14,color='currentColor'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <path fill="currentColor" d="M8.7 3.3 4.2 8.8l4.5 4.5 1.4-1.4L7.2 9.5h11.6l-4.4-4.4zM15.3 3.3l4.5 4.5-4.5 4.5-1.4-1.4L17.8 9.5H6.2l4.4-4.4z" opacity=".9"></path>
    </svg>
  );
}
function ApiLinkIcon({size=36,color='rgb(99,115,129)'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <path fill="currentColor" d="M10.6 6.6a4 4 0 0 1 5.66 0l1.14 1.14a4 4 0 0 1 0 5.66l-1.7 1.7a4 4 0 0 1-5.66 0" opacity=".45"></path>
      <path fill="currentColor" d="M13.4 17.4a4 4 0 0 1-5.66 0L6.6 16.26a4 4 0 0 1 0-5.66l1.7-1.7a4 4 0 0 1 5.66 0m-4.35 6.47 4.95-4.95"></path>
    </svg>
  );
}
function ApiSearchIcon({size=20,color='rgb(99,115,129)'}){
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{display:'inline-flex',verticalAlign:'middle',flexShrink:0,color}}>
      <g fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="11.5" cy="11.5" r="9.5"></circle>
        <path strokeLinecap="round" d="M18.5 18.5L22 22"></path>
      </g>
    </svg>
  );
}

/* ===== API MANAGEMENT PAGES =====
 *
 * Surfaces split across four leaves under the "API Management" sidebar group:
 *
 *   • ApiProxiesPage         — every REST/WEBHOOK integration surfaced as an API Proxy.
 *                              Copy public URL, see auth type, and edit the proxy IN PLACE via
 *                              ApiProxyEditorModal (name, public path). Java DSL code editing
 *                              is also in-place via NewRouteBuilderModal (the code button on
 *                              each row). The full integration editor (connections, trigger)
 *                              is still one click away.
 *
 *   • WebhookDeliveriesPage  — global delivery log (the per-integration view lives
 *                              inline in the Integrations panel via WebhookReceiverPanel).
 *                              Filter, inspect payload, replay.
 *
 *   • ApiKeysPage            — issue / rotate / revoke API keys. The plain-text secret
 *                              is shown EXACTLY ONCE on create/rotate; thereafter only
 *                              the SHA-256 hash sits on disk.
 *
 *   • DevelopersPage         — read-only roll-up of who's calling what — derived from
 *                              the rate-limit buckets (precise last 60s) and the
 *                              ApiKey.lastUsedAt timestamp (cumulative).
 *
 * The OpenAPI 3.0 spec for the live REST integrations is exposed at
 *   GET /api/admin/openapi
 * and downloaded via the button on the API Proxies page.
 *
 * All four components rely on the global API() helper + showToast() prop set up by App.
 */

/* ── API Proxies — filtered Integrations view focused on REST/WEBHOOK ── */
function ApiProxiesPage({showToast,navFilter,clearNavFilter,goToIntegration}){
  const [integrations,setIntegrations]=useState([]);
  const [loading,setLoading]=useState(true);
  const [q,setQ]=useState('');
  const [authFilter,setAuthFilter]=useState('all');
  // Endpoint opened in the in-place editor. Editing used to bounce the operator
  // out to Develop → Integrations; the modal keeps the loop inside API Management.
  const [editing,setEditing]=useState(null);
  const [testing,setTesting]=useState(null);
  const [testTab,setTestTab]=useState('body');
  const [testMethod,setTestMethod]=useState('POST');
  const [testUrl,setTestUrl]=useState('');
  const [testHeaders,setTestHeaders]=useState('{\n  "Content-Type": "application/json"\n}');
  const [testBody,setTestBody]=useState('{\n  \n}');
  const [testResp,setTestResp]=useState(null);
  const [testBusy,setTestBusy]=useState(false);
  // Code editor for Java DSL proxy integrations — opens NewRouteBuilderModal
  // in-place so the operator stays inside API Management.
  const [showCodeBuilder,setShowCodeBuilder]=useState(false);
  const [codeEditInt,setCodeEditInt]=useState(null);
  // When App lands here from an Integrations row chip, highlight that row.
  // Cleared after first paint so subsequent navigations don't keep the glow.
  const [highlightId,setHighlightId]=useState(null);
  useEffect(()=>{
    if(navFilter && navFilter.integrationId){
      setHighlightId(navFilter.integrationId);
      // Drop the App-level filter once consumed so we don't re-highlight if
      // the user navigates away and back without a fresh deep-link.
      if(clearNavFilter) setTimeout(clearNavFilter,50);
      // Auto-clear the local highlight after a short pulse — same UX as the
      // Integrations-side highlight set up via the Projects → Integration jump.
      const t=setTimeout(()=>setHighlightId(null),3500);
      return()=>clearTimeout(t);
    }
  },[navFilter,clearNavFilter]);

  const reload=useCallback(async()=>{
    setLoading(true);
    try{
      const list=await API('GET','/api/integrations');
      setIntegrations(list.filter(i=>{
        const t=(i.triggerType||'').toUpperCase();
        return t==='REST'||t==='WEBHOOK'||t==='REST_API';
      }));
    }catch(e){showToast&&showToast(e.message,'error');}
    finally{setLoading(false);}
  },[showToast]);
  useEffect(()=>{reload();},[reload]);

  const downloadOpenApi=async()=>{
    try{
      const url=`/api/admin/openapi?host=${encodeURIComponent(window.location.origin)}`;
      const resp=await fetch(url,{credentials:'same-origin'});
      if(!resp.ok)throw new Error('OpenAPI export failed: '+resp.status);
      const blob=await resp.blob();
      const a=document.createElement('a');
      const dl=URL.createObjectURL(blob);
      a.href=dl;a.download='openapi.json';document.body.appendChild(a);a.click();
      setTimeout(()=>{URL.revokeObjectURL(dl);a.remove();},500);
      showToast&&showToast('OpenAPI spec downloaded','success');
    }catch(e){showToast&&showToast(e.message,'error');}
  };
  const openTest=(i)=>{
    const p=buildPath(i);
    setTesting(i);
    setTestTab('body');
    setTestMethod('POST');
    setTestUrl(`${window.location.origin}/api${p.startsWith('/')?p:'/'+p}`);
    setTestHeaders('{\n  "Content-Type": "application/json"\n}');
    setTestBody('{\n  \n}');
    setTestResp(null);
  };
  const closeTest=()=>setTesting(null);
  const sendTest=async()=>{
    setTestBusy(true);
    setTestResp(null);
    try{
      let headers={};let body=null;
      try{ headers=JSON.parse(testHeaders||'{}'); }catch{ headers={'Content-Type':'application/json'}; }
      if(testBody && testBody.trim()){ try{ body=JSON.stringify(JSON.parse(testBody)); }catch{ body=testBody; } }
      const result=await rawFetch(testMethod,testUrl,{headers,body});
      setTestResp({...result,endpoint:testing});
    }catch(e){
      setTestResp({status:0,statusText:'Network error',body:{error:e.message||String(e)},isJson:true,ms:0,headers:{}});
    }finally{setTestBusy(false);}
  };

  const filtered=integrations.filter(i=>{
    if(q && !(i.name||'').toLowerCase().includes(q.toLowerCase())
        && !(i.id||'').toLowerCase().includes(q.toLowerCase()))return false;
    if(authFilter!=='all'){
      const src=(i.javaRouteSource||'').toLowerCase();
      if(authFilter==='apikey'&&!src.includes('apikeyverifier'))return false;
      if(authFilter==='jwt'&&(src.includes('taxlabjwtverifier')||src.includes('taxlab_jwt')))return false;
      if(authFilter==='none'&&(src.includes('apikeyverifier')||src.includes('taxlabjwtverifier')))return false;
    }
    return true;
  });
  const isJurist=(i)=> (i.builderClassName||'').toLowerCase().includes('uaeproxyroutes')
    || (i.name||'').toLowerCase().startsWith('proxy-uae-');
  const jurist=filtered.filter(isJurist);
  const other=filtered.filter(i=>!isJurist(i));

  const buildPath=(i)=>{
    let cfg={};
    try{cfg=i.triggerConfig?JSON.parse(i.triggerConfig):{};}catch{cfg={};}
    return cfg.path||`/webhooks/${i.id}`;
  };
  const fullUrl=(i)=>{
    const p=buildPath(i);
    return `${window.location.origin}/api${p.startsWith('/')?p:'/'+p}`;
  };
  const authBadge=(i)=>{
    const src=(i.javaRouteSource||'').toLowerCase();
    if(src.includes('apikeyverifier'))return <span className="badge badge-success" style={{fontSize:10}}>API Key</span>;
    if(src.includes('taxlabjwtverifier'))return <span className="badge badge-info" style={{fontSize:10}}>JWT (HS256)</span>;
    return <span className="badge badge-gray" style={{fontSize:10}}>None</span>;
  };
  const statusBadge=(s)=>{
    const m={'ACTIVE':'success','DRAFT':'gray','INACTIVE':'warning','ERROR':'error','DEPLOYING':'info'};
    return <span className={`badge badge-${m[s]||'gray'}`} style={{fontSize:10}}>{s}</span>;
  };
  const copy=async(u)=>{
    try{await navigator.clipboard.writeText(u);showToast&&showToast('URL copied','success');}
    catch{showToast&&showToast('Copy failed','error');}
  };

  return(
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">API Proxies</h1>
        <div className="page-actions">
          <button className="btn btn-refresh" style={TAXLAB_LIGHT_STYLE} onClick={downloadOpenApi}>⬇ OpenAPI Spec</button>
          <button className="btn btn-refresh" style={TAXLAB_LIGHT_STYLE} onClick={reload}><BuilderRefreshIcon size={18}/> Refresh</button>
          {goToIntegration && (
            <button className="btn btn-primary" style={TAXLAB_CTA_STYLE} onClick={()=>goToIntegration({create:true})}
              title="Create a new API proxy — opens the visual route builder (Karavan / YAML)">
              ＋ New Proxy
            </button>
          )}
        </div>
      </div>
      <div style={{color:'var(--text2)',fontSize:13,marginBottom:14,maxWidth:780}}>
        Every API proxy exposed by this platform — i.e. integrations whose trigger type is REST or Webhook.
        Use the auth filter to see which proxies sit behind an API key, behind JWT, or are unauthenticated.
        Click a proxy to edit it here — name and public path. Java DSL editing moved to the Integrations view.
      </div>

      <div style={{display:'flex',gap:10,marginBottom:14,flexWrap:'wrap',alignItems:'center'}}>
        <div className="search-field" style={{width:300}}>
          <span className="search-field-icon"><ApiSearchIcon/></span>
          <input className="search-field-input" placeholder="Search by name or id…" value={q} onChange={e=>setQ(e.target.value)}/>
        </div>
        <select className="form-select" value={authFilter} onChange={e=>setAuthFilter(e.target.value)} style={{width:180}}>
          <option value="all">All auth types</option>
          <option value="apikey">API Key only</option>
          <option value="jwt">JWT only</option>
          <option value="none">No auth</option>
        </select>
        <div style={{flex:1}}/>
        <span style={{fontSize:12,color:'var(--text3)'}}>
          {filtered.length} proxy{integrations.length===1?'':'ies'}
          {jurist.length>0 && <span style={{marginLeft:6,color:'var(--brand-start)'}}>({jurist.length} Jurist)</span>}
        </span>
      </div>

      {loading && <div style={{color:'var(--text3)'}}>Loading…</div>}
      {!loading && filtered.length===0 && (
        <div className="empty-state">
          <div className="empty-icon" style={{background:'rgba(14,165,233,.16)',color:'rgb(3,105,161)'}}><ApiLinkIcon size={24} color="currentColor"/></div>
          <div style={{fontWeight:600,marginBottom:6}}>No API proxies yet</div>
          <div style={{color:'var(--text3)',fontSize:13}}>Create a REST integration to expose a proxy on this platform.</div>
          {goToIntegration && <button className="btn btn-primary btn-sm" style={{...TAXLAB_CTA_STYLE,marginTop:12}} onClick={()=>goToIntegration({create:true})}>＋ New Proxy</button>}
        </div>
      )}
      {!loading && filtered.length>0 && (
        <div className="card" style={{padding:0,overflowX:'auto'}}>
          <table style={{width:'100%',minWidth:900,fontSize:13,borderCollapse:'collapse'}}>
            <thead>
              <tr style={{borderBottom:'1px solid var(--border)',color:'var(--text3)',textAlign:'left',fontSize:11,textTransform:'uppercase',letterSpacing:'.5px'}}>
                <th style={{padding:'10px 12px',fontWeight:600}}>Proxy</th>
                <th style={{padding:'10px 12px',fontWeight:600}}>URL</th>
                <th style={{padding:'10px 12px',fontWeight:600}}>Auth</th>
                <th style={{padding:'10px 12px',fontWeight:600}}>Status</th>
                <th style={{padding:'10px 12px',fontWeight:600,textAlign:'right'}}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {jurist.length>0 && (
                <tr>
                  <td colSpan={5} style={{padding:'8px 12px',background:'var(--surface2)',fontSize:11,fontWeight:700,
                    textTransform:'uppercase',letterSpacing:'.7px',color:'var(--brand-start)',
                    borderTop:'2px solid var(--brand-grad,var(--border))'}}>
                    ⚖ Jurist — TaxLab ASP passthrough proxies ({jurist.length})
                  </td>
                </tr>
              )}
              {jurist.map(i=>{
                const isHighlighted = highlightId===i.id;
                return(
                <tr key={i.id} id={`endpoint-${i.id}`}
                  ref={el=>{ if(el && isHighlighted) el.scrollIntoView({behavior:'smooth',block:'center'}); }}
                  onClick={()=>setEditing(i)}
                  style={{
                    borderBottom:'1px solid var(--border)',
                    cursor:'pointer',
                    background: isHighlighted ? 'rgba(8,145,178,.06)' : 'rgba(139,92,246,.02)',
                    ...(isHighlighted?{outline:'2px solid #0891b2',outlineOffset:-2}:{})
                  }}>
                  <td style={{padding:'10px 12px'}}>
                    <div style={{fontWeight:600}}>{i.name||i.id}</div>
                    <div style={{fontSize:11,color:'var(--text3)',fontFamily:'ui-monospace,monospace'}}>{i.id}</div>
                  </td>
                  <td style={{padding:'10px 12px',fontFamily:'ui-monospace,monospace',fontSize:11.5}}>
                    <span style={{color:'var(--text2)'}}>{fullUrl(i)}</span>
                  </td>
                  <td style={{padding:'10px 12px'}}>{authBadge(i)}</td>
                  <td style={{padding:'10px 12px'}}>{statusBadge(i.status)}</td>
                  <td style={{padding:'12px 12px',textAlign:'right',whiteSpace:'nowrap'}}
                      onClick={ev=>ev.stopPropagation()}>
                    <ProxyRowActions
                      onTest={()=>openTest(i)}
                      onCopy={()=>copy(fullUrl(i))}
                      onEdit={()=>setEditing(i)}
                      onCode={()=>{setCodeEditInt(i);setShowCodeBuilder(true);}}
                      onOpen={goToIntegration?()=>goToIntegration({integrationId:i.id}):null}
                    />
                  </td>
                </tr>
              );})}
              {other.length>0 && jurist.length>0 && (
                <tr>
                  <td colSpan={5} style={{padding:'6px 12px',background:'var(--surface)',fontSize:10,fontWeight:600,
                    textTransform:'uppercase',letterSpacing:'.6px',color:'var(--text3)',
                    borderTop:'1px solid var(--border)',borderBottom:'1px solid var(--border)'}}>
                    Other proxies
                  </td>
                </tr>
              )}
              {other.map(i=>{
                const isHighlighted = highlightId===i.id;
                return(
                <tr key={i.id} id={`endpoint-${i.id}`}
                  ref={el=>{ if(el && isHighlighted) el.scrollIntoView({behavior:'smooth',block:'center'}); }}
                  onClick={()=>setEditing(i)}
                  style={{
                    borderBottom:'1px solid var(--border)',
                    cursor:'pointer',
                    ...(isHighlighted?{outline:'2px solid #0891b2',outlineOffset:-2,background:'rgba(8,145,178,.06)'}:{})
                  }}>
                  <td style={{padding:'10px 12px'}}>
                    <div style={{fontWeight:600}}>{i.name||i.id}</div>
                    <div style={{fontSize:11,color:'var(--text3)',fontFamily:'ui-monospace,monospace'}}>{i.id}</div>
                  </td>
                  <td style={{padding:'10px 12px',fontFamily:'ui-monospace,monospace',fontSize:11.5}}>
                    <span style={{color:'var(--text2)'}}>{fullUrl(i)}</span>
                  </td>
                  <td style={{padding:'10px 12px'}}>{authBadge(i)}</td>
                  <td style={{padding:'10px 12px'}}>{statusBadge(i.status)}</td>
                  <td style={{padding:'12px 12px',textAlign:'right',whiteSpace:'nowrap'}}
                      onClick={ev=>ev.stopPropagation()}>
                    <ProxyRowActions
                      onTest={()=>openTest(i)}
                      onCopy={()=>copy(fullUrl(i))}
                      onEdit={()=>setEditing(i)}
                      onCode={()=>{setCodeEditInt(i);setShowCodeBuilder(true);}}
                      onOpen={goToIntegration?()=>goToIntegration({integrationId:i.id}):null}
                    />
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <ApiProxyEditorModal
          integration={editing}
          onClose={()=>setEditing(null)}
          onSaved={reload}
          showToast={showToast}
          goToIntegration={goToIntegration}/>
      )}

      {testing && (
        <TestConsole
          testing={testing}
          testTab={testTab} setTestTab={setTestTab}
          testMethod={testMethod} setTestMethod={setTestMethod}
          testUrl={testUrl} setTestUrl={setTestUrl}
          testHeaders={testHeaders} setTestHeaders={setTestHeaders}
          testBody={testBody} setTestBody={setTestBody}
          testResp={testResp}
          testBusy={testBusy}
          onClose={closeTest} onSend={sendTest}/>
      )}

      {showCodeBuilder && codeEditInt && (
        <NewRouteBuilderModal
          initial={codeEditInt}
          onClose={()=>{setShowCodeBuilder(false);setCodeEditInt(null);}}
          onSaved={()=>{setShowCodeBuilder(false);setCodeEditInt(null);reload();}}
          showToast={showToast}
          projects={[]}/>
      )}
    </div>
  );
}

/* ── Test Console — Postman-like HTTP client embedded in API Management ────
 *
 * Slide-out panel that lets operators fire arbitrary HTTP requests against any
 * registered endpoint without leaving the UI or opening Postman / curl.
 *
 * Layout mirrors Postman's left-to-right: method + URL bar at top, tabbed
 * request builder (Headers / Body) below, response panel on the right split.
 *
 * Response formatting:
 *   • JSON → syntax-highlighted, collapsible
 *   • Plain text / HTML → <pre> block
 *   • Non-text → hex dump of the first 4 KB
 *
 * No backend changes required — rawFetch() uses the browser's fetch API directly
 * against the same-origin proxy.
 */
function TestConsole({testing,testTab,setTestTab,testMethod,setTestMethod,testUrl,setTestUrl,
  testHeaders,setTestHeaders,testBody,setTestBody,testResp,testBusy,onClose,onSend}){
  const METHODS=['GET','POST','PUT','PATCH','DELETE','HEAD','OPTIONS'];
  const fmtJson=(obj,indent=0)=>{
    if(obj==null)return <span style={{color:'#6b7280'}}>null</span>;
    if(typeof obj==='boolean')return <span style={{color:'#0891b2'}}>{String(obj)}</span>;
    if(typeof obj==='number')return <span style={{color:'#0891b2'}}>{obj}</span>;
    if(typeof obj==='string')return <span style={{color:'#16a34a'}}>{JSON.stringify(obj)}</span>;
    if(Array.isArray(obj)){
      if(!obj.length)return <span style={{color:'#6b7280'}}>[]</span>;
      return(<pre style={{margin:0,paddingLeft:indent?16:0}}>[{obj.map((v,i)=>
        <div key={i} style={{paddingLeft:indent?12:0}}>{fmtJson(v,indent+1)}{i<obj.length-1?',':''}</div>
      )}]</pre>);
    }
    const keys=Object.keys(obj);
    if(!keys.length)return <span style={{color:'#6b7280'}}>{'{ }'}</span>;
    return(<pre style={{margin:0,paddingLeft:indent?16:0}}>{'{'}{keys.map((k,i)=>
      <div key={k} style={{paddingLeft:indent?12:0}}>
        <span style={{color:'#7c3aed'}}>{k}</span>: {fmtJson(obj[k],indent+1)}{i<keys.length-1?',':''}
      </div>
    )}{'}'}</pre>);
  };
  const fmtBytes=(buf)=>{
    const arr=new Uint8Array(buf);
    let hex='';const max=4096;const len=Math.min(arr.length,max);
    for(let i=0;i<len;i+=16){
      hex+=Array.from(arr.slice(i,i+16)).map(b=>b.toString(16).padStart(2,'0')).join(' ').padEnd(48);
      const chunk=String.fromCharCode.apply(null,arr.slice(i,i+16).filter(b=>b>=32&&b<127));
      hex+=' | '+chunk+'\n';
    }
    if(arr.length>max)hex+=`\n… ${(arr.length-max).toLocaleString()} more bytes`;
    return hex;
  };
  const renderResponse=(r)=>{
    if(!r)return <div style={{color:'var(--text3)',fontSize:12,padding:20}}>Send a request to see the response here.</div>;
    const statusColor=(r.status<300?'#16a34a':r.status<400?'#0891b2':r.status<500?'#d97706':'#dc2626');
    return(<div style={{fontSize:12,fontFamily:'ui-monospace,monospace'}}>
      <div style={{display:'flex',gap:12,alignItems:'center',marginBottom:10,paddingBottom:8,borderBottom:'1px solid var(--border)'}}>
        <span style={{fontSize:18,fontWeight:700,color:statusColor}}>{r.status}</span>
        <span style={{color:'var(--text2)'}}>{r.statusText}</span>
        <span style={{color:'var(--text3)',marginLeft:'auto'}}>{r.ms} ms</span>
      </div>
      {r.isJson && typeof r.body==='object' ? (
        <div style={{background:'#0f172a',color:'#e2e8f0',padding:12,borderRadius:6,overflow:'auto',maxHeight:400,fontSize:11,lineHeight:1.6}}>
          {fmtJson(r.body)}
        </div>
      ) : typeof r.body==='string' ? (
        <pre style={{background:'#f8fafc',color:'#1e293b',padding:12,borderRadius:6,overflow:'auto',maxHeight:400,fontSize:11,whiteSpace:'pre-wrap',wordBreak:'break-word'}}>{r.body}</pre>
      ) : (
        <pre style={{background:'#f8fafc',color:'#1e293b',padding:12,borderRadius:6,overflow:'auto',maxHeight:400,fontSize:11}}>{fmtBytes(r.body)}</pre>
      )}
    </div>);
  };
  const tabCls=(t)=>`test-tab ${testTab===t?'active':''}`;
  return(
    <div className="modal-overlay" onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div className="modal modal-xl" style={{maxWidth:960}}>
        <div className="modal-header">
          <div className="modal-title">⚡ Test — {(testing||{}).name||(testing||{}).id}</div>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close"><CloseIcon size={16}/></button>
        </div>
        <div className="modal-body">
        <div className="modal-body-scroll">
          {/* ── Request bar ─────────────────────────────────────────────── */}
          <div style={{display:'flex',gap:6,marginBottom:12}}>
            <select className="form-select" value={testMethod} onChange={e=>setTestMethod(e.target.value)}
              style={{width:110,fontFamily:'ui-monospace,monospace',fontWeight:600}}>
              {METHODS.map(m=><option key={m} value={m}>{m}</option>)}
            </select>
            <input className="form-input" value={testUrl} onChange={e=>setTestUrl(e.target.value)}
              placeholder="https://example.com/api/endpoint"
              style={{flex:1,fontFamily:'ui-monospace,monospace',fontSize:12}}/>
            <button className="btn btn-primary" onClick={onSend} disabled={testBusy||!testUrl.trim()}
              style={{minWidth:80}}>
              {testBusy?'Sending…':'Send'}
            </button>
          </div>

          {/* ── Tabs: Body | Headers | Response ─────────────────────────── */}
          <div style={{display:'flex',gap:0,borderBottom:'1px solid var(--border)',marginBottom:0}}>
            <button className={tabCls('body')} onClick={()=>setTestTab('body')}>Body</button>
            <button className={tabCls('headers')} onClick={()=>setTestTab('headers')}>Headers</button>
            <button className={tabCls('response')} onClick={()=>setTestTab('response')}
              style={{position:'relative'}}>
              Response
              {testResp && <span style={{marginLeft:6,background:testResp.status<300?'#16a34a':testResp.status<400?'#0891b2':'#dc2626',
                color:'#fff',borderRadius:10,padding:'1px 7px',fontSize:10}}>{testResp.status}</span>}
            </button>
          </div>

          <div style={{minHeight:200,maxHeight:420}}>
            {testTab==='body' && (
              <div style={{padding:'8px 0'}}>
                <div style={{fontSize:11,color:'var(--text3)',marginBottom:6}}>Request body (JSON, raw text, or leave empty)</div>
                <textarea className="form-input mono" value={testBody} onChange={e=>setTestBody(e.target.value)}
                  placeholder='{"key": "value"}'
                  rows={10} spellCheck={false}
                  style={{fontFamily:'ui-monospace,monospace',fontSize:12,resize:'vertical',lineHeight:1.5}}/>
              </div>
            )}
            {testTab==='headers' && (
              <div style={{padding:'8px 0'}}>
                <div style={{fontSize:11,color:'var(--text3)',marginBottom:6}}>Request headers (JSON object)</div>
                <textarea className="form-input mono" value={testHeaders} onChange={e=>setTestHeaders(e.target.value)}
                  placeholder='{"Content-Type": "application/json"}'
                  rows={6} spellCheck={false}
                  style={{fontFamily:'ui-monospace,monospace',fontSize:12,resize:'vertical',lineHeight:1.5}}/>
              </div>
            )}
            {testTab==='response' && (
              <div style={{padding:'4px 0'}}>
                {renderResponse(testResp)}
              </div>
            )}
          </div>
        </div>
        </div>
        <div className="modal-footer" style={{display:'flex',gap:8,alignItems:'center'}}>
          <span style={{fontSize:11,color:'var(--text3)',fontFamily:'ui-monospace,monospace'}}>
            {(testing||{}).id}
          </span>
          <div style={{flex:1}}/>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Close</button>
          <button className="btn btn-primary btn-sm" onClick={onSend} disabled={testBusy||!testUrl.trim()}>
            {testBusy?'Sending…':'⚡ Send'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Endpoint editor — change an endpoint without leaving API Management ──
 *
 * Editing used to be a one-way trip out to Develop → Integrations. This modal
 * covers what an API owner actually changes on an endpoint — its name, the
 * public path subscribers call, and the Camel route behind it. The save
 * validates against the live classpath via the same integration endpoint.
 *
 * Java DSL editing is done in the full integration editor (one click via
 * "↗ Full editor").
 *
 * Two behaviours worth knowing:
 *   • Saving an ACTIVE endpoint re-activates it, so the RUNNING route matches what was
 *     just saved. This is safe for platform-http (REST/webhook) routes now that the engine
 *     unregisters the stale Spring MVC mapping on teardown (that gap used to 500 the
 *     endpoint on any in-place reload).
 *   • The public path is editable for all integrations (previously read-only for
 *     simple-mode integrations). Changing it changes the public URL.
 */
function ApiProxyEditorModal({integration,onClose,onSaved,showToast,goToIntegration}){
  const [full,setFull]=useState(null);
  const [loading,setLoading]=useState(true);
  const [name,setName]=useState('');
  const [description,setDescription]=useState('');
  const [path,setPath]=useState('');
  const [saving,setSaving]=useState(false);

  const pathOf=(i)=>{ let c={}; try{c=i.triggerConfig?JSON.parse(i.triggerConfig):{};}catch{c={};} return c.path||''; };

  useEffect(()=>{
    let dead=false;
    (async()=>{
      setLoading(true);
      try{
        const resp=await API('GET',`/api/integrations/${encodeURIComponent(integration.id)}`);
        const i=(resp&&resp.integration)?resp.integration:resp;
        if(dead)return;
        setFull(i); setName(i.name||''); setDescription(i.description||'');
        setPath(pathOf(i));
      }catch(e){ if(!dead){ showToast&&showToast(e.message,'error'); onClose(); } }
      finally{ if(!dead) setLoading(false); }
    })();
    return()=>{dead=true;};
  },[integration.id]);

  const hasJava  = !!(full && full.builderClassName);
  const isActive = !!(full && (full.status||'').toUpperCase()==='ACTIVE');
  const dirty    = !!full && (name!==(full.name||'') || description!==(full.description||'')
                     || path!==pathOf(full));
  const publicUrl= `${window.location.origin}/api${path.startsWith('/')?path:'/'+path}`;

  const save=async()=>{
    if(!full)return;
    setSaving(true);
    try{
      let cfg={}; try{cfg=full.triggerConfig?JSON.parse(full.triggerConfig):{};}catch{cfg={};}
      if(path) cfg.path=path;
      const body={...full,name,description,triggerConfig:JSON.stringify(cfg)};
      await API('PUT',`/api/integrations/${encodeURIComponent(full.id)}`,body);
      if(isActive){
        // Re-activate so the RUNNING route matches what was just saved. Platform-http
        // (REST/webhook) routes now hot-swap cleanly — the engine unregisters the stale
        // Spring MVC mapping on teardown (previously this 500'd the endpoint).
        await API('POST',`/api/integrations/${encodeURIComponent(full.id)}/activate`,{});
        showToast&&showToast('Saved — live route redeployed','success');
      }else{
        showToast&&showToast('Saved','success');
      }
      onSaved&&onSaved();
      onClose();
    }catch(e){ showToast&&showToast(e.message,'error'); }
    finally{ setSaving(false); }
  };

  return(
    <div className="modal-overlay" onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div className="modal modal-lg">
        <div className="modal-header">
          <div className="modal-title">Edit proxy — {integration.name||integration.id}</div>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close"><CloseIcon size={16}/></button>
        </div>
        <div className="modal-body">
        <div className="modal-body-scroll">
          {loading && <div style={{color:'var(--text3)'}}>Loading…</div>}
          {!loading && full && (
            <>
              <div style={{display:'grid',gridTemplateColumns:'auto 1fr',gap:'6px 14px',fontSize:12,marginBottom:16}}>
                <div style={{color:'var(--text3)'}}>Integration id</div>
                <div style={{fontFamily:'ui-monospace,monospace'}}>{full.id}</div>
                <div style={{color:'var(--text3)'}}>Status</div>
                <div>{full.status}{isActive&&<span style={{color:'var(--text3)',marginLeft:8}}>— saving redeploys the live route</span>}</div>
                <div style={{color:'var(--text3)'}}>Public URL</div>
                <div style={{fontFamily:'ui-monospace,monospace',wordBreak:'break-all'}}>{publicUrl}</div>
              </div>

              <div className="form-group">
                <label className="form-label">Name</label>
                <input className="form-input" value={name} onChange={e=>setName(e.target.value)}/>
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-input" rows={2} value={description}
                  onChange={e=>setDescription(e.target.value)}/>
              </div>
              <div className="form-group">
                <label className="form-label">Public path</label>
                <input className="form-input" value={path} onChange={e=>setPath(e.target.value)}
                  placeholder="/uae-invoice/v1/documents"
                  style={{fontFamily:'ui-monospace,monospace'}}/>
                <div style={{fontSize:11,color:'var(--text3)',marginTop:4}}>
                  Changing this changes the URL your subscribers call.
                </div>
              </div>

              {hasJava && (
                <div style={{fontSize:12,color:'var(--text3)',background:'var(--surface2)',padding:10,borderRadius:6,
                  display:'flex',alignItems:'center',gap:8}}>
                  <span style={{fontFamily:'ui-monospace,monospace',fontSize:11}}>{full.builderClassName}</span>
                  {goToIntegration && (
                    <button className="btn btn-ghost btn-xs"
                      onClick={()=>{onClose();goToIntegration({integrationId:integration.id});}}
                      title="Edit Java DSL, connections, and trigger in the full integration editor"
                      style={{marginLeft:'auto'}}>
                      Edit in integration editor
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        </div>
        <div className="modal-footer" style={{display:'flex',gap:8,alignItems:'center'}}>
          {goToIntegration && (
            <button className="btn btn-ghost btn-sm"
              onClick={()=>{onClose();goToIntegration({integrationId:integration.id});}}
              title="Connections, trigger type, Java DSL editor">↗ Full editor</button>
          )}
          <div style={{flex:1}}/>
          <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving||loading||!dirty}>
            {saving?'Saving…':(isActive?'Save & redeploy':'Save')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Webhook Deliveries — global view across all integrations ─────────── */
function WebhookDeliveriesPage({showToast,confirm}){
  const [rows,setRows]=useState([]);
  const [summary,setSummary]=useState(null);
  const [loading,setLoading]=useState(false);
  const [statusFilter,setStatusFilter]=useState('all');
  const [integrationFilter,setIntegrationFilter]=useState('');
  const [openDelivery,setOpenDelivery]=useState(null);
  const [replaying,setReplaying]=useState({});

  const reload=useCallback(async()=>{
    setLoading(true);
    try{
      const status=statusFilter==='all'?'':statusFilter;
      const qs=new URLSearchParams();
      if(status)qs.set('status',status);
      if(integrationFilter)qs.set('integrationId',integrationFilter);
      qs.set('limit','100');
      const [s,d]=await loadAll([
        ['Delivery summary',API('GET','/api/admin/webhook-deliveries/summary'+(integrationFilter?`?integrationId=${encodeURIComponent(integrationFilter)}`:'')),null],
        ['Deliveries',API('GET','/api/admin/webhook-deliveries?'+qs.toString()),[]],
      ],showToast);
      setSummary(s);
      setRows(d);
    }catch(e){showToast&&showToast(e.message,'error');}
    finally{setLoading(false);}
  },[statusFilter,integrationFilter,showToast]);
  useEffect(()=>{reload();},[reload]);

  const replay=async(id)=>{
    if(!await confirm('Replay this delivery? It will re-run the downstream handler with the original payload.'))return;
    setReplaying(p=>({...p,[id]:true}));
    try{
      await API('POST',`/api/admin/webhook-deliveries/${encodeURIComponent(id)}/replay`);
      showToast&&showToast('Replay dispatched','success');
      setTimeout(reload,800);
    }catch(e){showToast&&showToast(e.message,'error');}
    finally{setReplaying(p=>({...p,[id]:false}));}
  };

  const statusBadge=(s)=>{
    const m={'SUCCESS':'success','RECEIVED':'info','RETRYING':'warning','FAILED':'error','DUPLICATE':'gray'};
    return <span className={`badge badge-${m[s]||'gray'}`} style={{fontSize:10}}>{s}</span>;
  };

  return(
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Webhook Deliveries</h1>
        <div className="page-actions">
          <button className="btn btn-refresh" style={TAXLAB_LIGHT_STYLE} onClick={reload}><BuilderRefreshIcon size={18}/> Refresh</button>
        </div>
      </div>
      <div style={{color:'var(--text2)',fontSize:13,marginBottom:14,maxWidth:780}}>
        Every inbound webhook delivery received by this platform. Click any row to inspect the
        raw payload. Failed deliveries can be replayed without asking the sender to retry.
      </div>

      {summary && summary.total>0 && (
        <div className="card" style={{padding:14,marginBottom:14}}>
          <div style={{display:'flex',gap:18,flexWrap:'wrap',alignItems:'center'}}>
            <div>
              <div style={{fontSize:11,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'.5px'}}>Total</div>
              <div style={{fontSize:22,fontWeight:700}}>{summary.total}</div>
            </div>
            {Object.entries(summary.countsByStatus||{}).map(([k,v])=>(
              <div key={k}>
                <div style={{fontSize:11,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'.5px'}}>{k}</div>
                <div style={{fontSize:22,fontWeight:700,display:'flex',alignItems:'center',gap:6}}>
                  {statusBadge(k)} <span>{v}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{display:'flex',gap:10,marginBottom:14,flexWrap:'wrap'}}>
        <select className="form-select" value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} style={{width:160}}>
          <option value="all">All statuses</option>
          <option value="SUCCESS">SUCCESS</option>
          <option value="RECEIVED">RECEIVED</option>
          <option value="FAILED">FAILED</option>
          <option value="RETRYING">RETRYING</option>
          <option value="DUPLICATE">DUPLICATE</option>
        </select>
        <input className="form-input" placeholder="Filter by integration id…" value={integrationFilter} onChange={e=>setIntegrationFilter(e.target.value)} style={{width:280}}/>
      </div>

      {loading && <div style={{color:'var(--text3)'}}>Loading…</div>}
      {!loading && rows.length===0 && (
        <div className="empty-state">
          <div className="empty-icon" style={{background:'rgba(16,185,129,.16)',color:'rgb(4,120,87)'}}><ApiInboxIcon size={24} color="currentColor"/></div>
          <div style={{fontWeight:600,marginBottom:6}}>No deliveries yet</div>
          <div style={{color:'var(--text3)',fontSize:13}}>Once external systems start posting to your webhook URLs they'll appear here.</div>
        </div>
      )}
      {!loading && rows.length>0 && (
        <div className="card" style={{padding:0,overflow:'hidden'}}>
          <table style={{width:'100%',fontSize:12,borderCollapse:'collapse'}}>
            <thead>
              <tr style={{borderBottom:'1px solid var(--border)',color:'var(--text3)',textAlign:'left',fontSize:11,textTransform:'uppercase',letterSpacing:'.5px'}}>
                <th style={{padding:'10px 12px'}}>Received</th>
                <th style={{padding:'10px 12px'}}>Integration</th>
                <th style={{padding:'10px 12px'}}>Event</th>
                <th style={{padding:'10px 12px'}}>Status</th>
                <th style={{padding:'10px 12px'}}>Attempts</th>
                <th style={{padding:'10px 12px',textAlign:'right'}}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(d=>(
                <tr key={d.deliveryId} style={{borderBottom:'1px solid var(--border)',cursor:'pointer'}}
                    onClick={()=>setOpenDelivery(d)}>
                  <td style={{padding:'10px 12px',fontFamily:'ui-monospace,monospace',color:'var(--text2)'}}>{fmtDate(d.receivedAt)}</td>
                  <td style={{padding:'10px 12px',fontFamily:'ui-monospace,monospace',fontSize:11}}>{d.integrationId}</td>
                  <td style={{padding:'10px 12px'}}>{d.eventType}</td>
                  <td style={{padding:'10px 12px'}}>{statusBadge(d.status)}</td>
                  <td style={{padding:'10px 12px',color:'var(--text2)'}}>{d.attemptCount||1}</td>
                  <td style={{padding:'10px 12px',textAlign:'right'}} onClick={ev=>ev.stopPropagation()}>
                    {(d.status==='FAILED'||d.status==='RETRYING') && (
                      <button className="int-row-btn int-row-btn-light" onClick={()=>replay(d.deliveryId)} disabled={replaying[d.deliveryId]}>
                        {replaying[d.deliveryId]?<Spinner/>:<><BuilderRefreshIcon size={17}/> Replay</>}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openDelivery && (
        <div className="modal-overlay" onClick={e=>{if(e.target===e.currentTarget)setOpenDelivery(null);}}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <div className="modal-title">Delivery {openDelivery.deliveryId}</div>
              <button className="modal-close-btn" onClick={()=>setOpenDelivery(null)} aria-label="Close"><CloseIcon size={16}/></button>
            </div>
            {/* .modal-body is a flex container with overflow:hidden; the
                actual scroll region is the nested .modal-body-scroll — without
                this wrapper, two-finger scroll inside the modal is dead. */}
            <div className="modal-body">
            <div className="modal-body-scroll">
              <div style={{display:'grid',gridTemplateColumns:'auto 1fr',gap:'6px 14px',fontSize:12,marginBottom:14}}>
                <div style={{color:'var(--text3)'}}>Integration</div><div style={{fontFamily:'ui-monospace,monospace'}}>{openDelivery.integrationId}</div>
                <div style={{color:'var(--text3)'}}>Event</div><div>{openDelivery.eventType}</div>
                <div style={{color:'var(--text3)'}}>Received</div><div>{fmtDate(openDelivery.receivedAt)}</div>
                <div style={{color:'var(--text3)'}}>Processed</div><div>{fmtDate(openDelivery.processedAt)||'—'}</div>
                <div style={{color:'var(--text3)'}}>Status</div><div>{statusBadge(openDelivery.status)}</div>
                <div style={{color:'var(--text3)'}}>Attempts</div><div>{openDelivery.attemptCount||1}</div>
                {openDelivery.errorMessage && <><div style={{color:'var(--text3)'}}>Error</div><div style={{color:'var(--error)'}}>{openDelivery.errorMessage}</div></>}
              </div>
              <div style={{fontSize:11,color:'var(--text3)',marginBottom:6,textTransform:'uppercase',letterSpacing:'.5px'}}>Payload</div>
              <pre style={{background:'var(--surface2)',padding:12,borderRadius:6,fontSize:11,overflow:'auto',maxHeight:400,whiteSpace:'pre-wrap',wordBreak:'break-word'}}>
                {openDelivery.eventPayload||openDelivery.eventPayloadPreview||'(empty)'}
              </pre>
            </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
